<script setup>
import { Head } from '@inertiajs/vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import { computed, ref, reactive } from 'vue';
import { trackEvent } from '@/lib/tracking';
import { useCopy } from '@/composables/useCopy';

const props = defineProps({
    domain: Object,
    service: String,
    branding: Object,
    designVariant: Object,
    copyPack: { type: Object, default: () => ({}) },
    spotImages: { type: Object, default: () => ({}) },
});

const { t, ts, spot } = useCopy(props);

const serviceLabel = computed(() => props.service === 'wp' ? 'Warmepumpen' : 'Photovoltaik');
const serviceSingular = computed(() => props.service === 'wp' ? 'Warmepumpe' : 'Solaranlage');

const form = reactive({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    postal_code: '',
    service_type: props.service || 'pv',
    offer_file: null,
    privacy_accepted: false,
});

const fileInputRef = ref(null);
const isDragging = ref(false);

const handleFileDrop = (e) => {
    isDragging.value = false;
    const file = e.dataTransfer?.files?.[0];
    if (file) form.offer_file = file;
};

const handleFileSelect = (e) => {
    const file = e.target?.files?.[0];
    if (file) form.offer_file = file;
};

const removeFile = () => {
    form.offer_file = null;
    if (fileInputRef.value) fileInputRef.value.value = '';
};

const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
};

const errors = ref({});
const submitting = ref(false);
const submitted = ref(false);

const submit = async () => {
    errors.value = {};
    if (!form.first_name) errors.value.first_name = t('offercompare_validate_first_name', 'Bitte gib deinen Vornamen ein.');
    if (!form.last_name) errors.value.last_name = t('offercompare_validate_last_name', 'Bitte gib deinen Nachnamen ein.');
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.value.email = t('offercompare_validate_email', 'Bitte gib eine gültige E-Mail-Adresse ein.');
    if (!form.postal_code || !/^\d{5}$/.test(form.postal_code)) errors.value.postal_code = t('offercompare_validate_postal_code', 'Bitte gib eine gültige PLZ ein.');
    if (!form.privacy_accepted) errors.value.privacy_accepted = t('offercompare_validate_privacy', 'Bitte stimme der Datenschutzerklärung zu.');
    if (Object.keys(errors.value).length > 0) return;

    submitting.value = true;
    trackEvent('offer_compare_submit', { event_category: 'conversion', service_type: form.service_type });

    try {
        const fd = new FormData();
        fd.append('first_name', form.first_name);
        fd.append('last_name', form.last_name);
        fd.append('email', form.email);
        fd.append('phone', form.phone);
        fd.append('postal_code', form.postal_code);
        fd.append('service_type', form.service_type);
        fd.append('privacy_accepted', form.privacy_accepted ? '1' : '0');
        if (form.offer_file) fd.append('offer_file', form.offer_file);

        const res = await fetch('/angebot-vergleichen', {
            method: 'POST',
            headers: {
                'X-XSRF-TOKEN': decodeURIComponent(document.cookie.match(/XSRF-TOKEN=([^;]+)/)?.[1] || ''),
                Accept: 'application/json',
            },
            body: fd,
        });

        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            if (data.errors) {
                errors.value = Object.fromEntries(Object.entries(data.errors).map(([k, v]) => [k, Array.isArray(v) ? v[0] : v]));
            } else {
                errors.value.general = t('offercompare_error_general', 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.');
            }
            return;
        }
        submitted.value = true;
    } catch {
        errors.value.general = t('offercompare_error_connection', 'Verbindungsfehler. Bitte versuche es erneut.');
    } finally {
        submitting.value = false;
    }
};
</script>

<template>
    <Head :title="`Angebot vergleichen - ${serviceLabel}`">
        <meta name="description" :content="`Lass dein ${serviceLabel}-Angebot kostenlos prüfen und vergleichen.`" />
    </Head>

    <AppLayout :domain="domain" :branding="branding" :designVariant="designVariant" :service="service" heroMode>
        <!-- Dark hero -->
        <section class="sv-oc-hero">
            <div class="sv-oc-hero__inner">
                <p class="sv-oc-hero__tag">{{ t('offercompare_hero_tag', 'Angebotsvergleich') }}</p>
                <h1 class="sv-oc-hero__title">{{ ts('offercompare_hero_title_pre', `Dein ${serviceSingular.value}-Angebot`) }} <span class="sv-oc-hero__accent">{{ t('offercompare_hero_title_accent', 'prüfen lassen') }}</span></h1>
                <p class="sv-oc-hero__sub">{{ t('offercompare_hero_sub', 'Du hast bereits ein Angebot erhalten? Wir prüfen es kostenlos und finden heraus, ob es fair ist.') }}</p>
            </div>
        </section>

        <!-- Form section -->
        <section class="sv-oc-body">
            <div class="sv-oc-container">
                <!-- Success -->
                <div v-if="submitted" class="sv-oc-success">
                    <div class="sv-oc-success__check">{{ t('offercompare_success_badge', '[OK]') }}</div>
                    <h2 class="sv-oc-success__title">{{ t('offercompare_success_title', 'Danke!') }}</h2>
                    <p class="sv-oc-success__text">{{ t('offercompare_success_text', 'Wir haben deine Anfrage erhalten und melden uns in Kürze.') }}</p>
                </div>

                <!-- Form -->
                <form v-else @submit.prevent="submit" class="sv-oc-form">
                    <h2 class="sv-oc-form__heading">{{ t('offercompare_form_heading', 'Deine Daten') }}</h2>
                    <p v-if="errors.general" class="sv-oc-form__alert">{{ errors.general }}</p>

                    <div class="sv-oc-form__grid">
                        <div class="sv-oc-form__field">
                            <label class="sv-oc-form__label">{{ t('offercompare_form_first_name', 'Vorname *') }}</label>
                            <input v-model="form.first_name" type="text" class="sv-oc-form__input" :placeholder="t('offercompare_form_first_name_placeholder', 'Max')" />
                            <span v-if="errors.first_name" class="sv-oc-form__err">{{ errors.first_name }}</span>
                        </div>
                        <div class="sv-oc-form__field">
                            <label class="sv-oc-form__label">{{ t('offercompare_form_last_name', 'Nachname *') }}</label>
                            <input v-model="form.last_name" type="text" class="sv-oc-form__input" :placeholder="t('offercompare_form_last_name_placeholder', 'Mustermann')" />
                            <span v-if="errors.last_name" class="sv-oc-form__err">{{ errors.last_name }}</span>
                        </div>
                    </div>
                    <div class="sv-oc-form__grid">
                        <div class="sv-oc-form__field">
                            <label class="sv-oc-form__label">{{ t('offercompare_form_email', 'E-Mail *') }}</label>
                            <input v-model="form.email" type="email" class="sv-oc-form__input" :placeholder="t('offercompare_form_email_placeholder', 'max@beispiel.de')" />
                            <span v-if="errors.email" class="sv-oc-form__err">{{ errors.email }}</span>
                        </div>
                        <div class="sv-oc-form__field">
                            <label class="sv-oc-form__label">{{ t('offercompare_form_phone', 'Telefon') }}</label>
                            <input v-model="form.phone" type="tel" class="sv-oc-form__input" :placeholder="t('offercompare_form_phone_placeholder', '0151 12345678')" />
                        </div>
                    </div>
                    <div class="sv-oc-form__field">
                        <label class="sv-oc-form__label">{{ t('offercompare_form_postal_code', 'PLZ *') }}</label>
                        <input v-model="form.postal_code" type="text" inputmode="numeric" maxlength="5" class="sv-oc-form__input sv-oc-form__input--short" :placeholder="t('offercompare_form_postal_code_placeholder', '10115')" />
                        <span v-if="errors.postal_code" class="sv-oc-form__err">{{ errors.postal_code }}</span>
                    </div>

                    <!-- File upload -->
                    <div class="sv-oc-form__field">
                        <label class="sv-oc-form__label">{{ t('offercompare_form_file_label', 'Angebot hochladen (optional)') }}</label>
                        <div
                            class="sv-oc-drop"
                            :class="{ 'sv-oc-drop--active': isDragging }"
                            @dragover.prevent="isDragging = true"
                            @dragleave.prevent="isDragging = false"
                            @drop.prevent="handleFileDrop"
                            @click="fileInputRef?.click()"
                        >
                            <template v-if="!form.offer_file">
                                <p class="sv-oc-drop__text">{{ t('offercompare_form_drop_text', 'Datei hierher ziehen oder klicken') }}</p>
                                <p class="sv-oc-drop__hint">{{ t('offercompare_form_drop_hint', 'PDF, JPG oder PNG (max. 10 MB)') }}</p>
                            </template>
                            <template v-else>
                                <p class="sv-oc-drop__file">{{ form.offer_file.name }} ({{ formatFileSize(form.offer_file.size) }})</p>
                                <button type="button" @click.stop="removeFile" class="sv-oc-drop__remove">{{ t('offercompare_form_drop_remove', 'Entfernen') }}</button>
                            </template>
                        </div>
                        <input ref="fileInputRef" type="file" accept=".pdf,.jpg,.jpeg,.png" style="display:none" @change="handleFileSelect" />
                    </div>

                    <div class="sv-oc-form__field sv-oc-form__field--check">
                        <label class="sv-oc-form__checklabel">
                            <input type="checkbox" v-model="form.privacy_accepted" />
                            {{ t('offercompare_form_privacy_pre', 'Ich habe die') }} <a href="/datenschutz" target="_blank" class="sv-oc-form__link">{{ t('offercompare_form_privacy_link_1', 'Datenschutzerklärung') }}</a> {{ t('offercompare_form_privacy_and', 'und') }} <a href="/agb" target="_blank" class="sv-oc-form__link">{{ t('offercompare_form_privacy_link_2', 'AGB') }}</a> {{ t('offercompare_form_privacy_post', 'gelesen und stimme zu. *') }}
                        </label>
                        <span v-if="errors.privacy_accepted" class="sv-oc-form__err">{{ errors.privacy_accepted }}</span>
                    </div>

                    <button type="submit" :disabled="submitting" class="sv-oc-form__btn">
                        {{ submitting ? t('offercompare_form_submit_loading', 'Wird gesendet...') : t('offercompare_form_submit', 'Kostenlos vergleichen') }}
                    </button>
                </form>
            </div>
        </section>
    </AppLayout>
</template>

<style scoped>
.sv-oc-hero { background: #0a1628; padding: 120px 24px 80px; text-align: left; }
.sv-oc-hero__inner { max-width: 780px; margin: 0 auto; }
.sv-oc-hero__tag { font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 700; color: var(--brand-accent, #00d782); text-transform: uppercase; letter-spacing: 2px; margin-bottom: 16px; }
.sv-oc-hero__title { font-family: 'Inter', sans-serif; font-size: clamp(28px, 4vw, 44px); font-weight: 800; color: #ffffff; line-height: 1.15; margin-bottom: 16px; }
.sv-oc-hero__accent { color: var(--brand-accent, #00d782); }
.sv-oc-hero__sub { font-family: 'Inter', sans-serif; font-size: 17px; color: #94a3b8; line-height: 1.7; max-width: 560px; }

.sv-oc-body { background: #f0f4f8; padding: 60px 24px 100px; }
.sv-oc-container { max-width: 640px; margin: 0 auto; }

.sv-oc-success { text-align: center; padding: 60px 0; }
.sv-oc-success__check { font-family: 'Inter', sans-serif; font-size: 24px; font-weight: 800; color: var(--brand-accent, #00d782); margin-bottom: 16px; }
.sv-oc-success__title { font-family: 'Inter', sans-serif; font-size: 28px; font-weight: 800; color: #1a1a1a; margin-bottom: 12px; }
.sv-oc-success__text { font-family: 'Inter', sans-serif; font-size: 16px; color: #64748b; }

.sv-oc-form { background: #ffffff; padding: 32px; border-radius: 16px; display: flex; flex-direction: column; gap: 20px; box-shadow: 0 4px 24px rgba(0,0,0,0.06); }
.sv-oc-form__heading { font-family: 'Inter', sans-serif; font-size: 20px; font-weight: 800; color: #1a1a1a; padding-bottom: 14px; border-bottom: 3px solid var(--brand-accent, #00d782); }
.sv-oc-form__alert { font-family: 'Inter', sans-serif; font-size: 14px; color: #ef4444; background: rgba(239, 68, 68, 0.06); padding: 12px; border-radius: 8px; }
.sv-oc-form__grid { display: grid; grid-template-columns: 1fr; gap: 20px; }
@media (min-width: 640px) { .sv-oc-form__grid { grid-template-columns: 1fr 1fr; } }
.sv-oc-form__field { display: flex; flex-direction: column; }
.sv-oc-form__field--check { flex-direction: column; }
.sv-oc-form__label { font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 600; color: #374151; margin-bottom: 6px; }
.sv-oc-form__checklabel { font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 500; color: #374151; display: flex; align-items: flex-start; gap: 8px; }
.sv-oc-form__input { width: 100%; padding: 12px 16px; background: #f8fafc; border: 2px solid #e2e8f0; border-radius: 12px; font-family: 'Inter', sans-serif; font-size: 15px; color: #1a1a1a; outline: none; transition: border-color 200ms; }
.sv-oc-form__input:focus { border-color: var(--brand-accent, #00d782); box-shadow: 0 0 0 3px rgba(0, 215, 130, 0.12); }
.sv-oc-form__input--short { max-width: 200px; }
.sv-oc-form__err { font-family: 'Inter', sans-serif; font-size: 13px; color: #ef4444; margin-top: 4px; }
.sv-oc-form__link { color: var(--brand-accent, #00d782); text-decoration: none; font-weight: 600; }

.sv-oc-drop { padding: 28px; border: 2px dashed #cbd5e1; border-radius: 12px; background: #f8fafc; text-align: center; cursor: pointer; transition: border-color 200ms, background 200ms; }
.sv-oc-drop:hover, .sv-oc-drop--active { border-color: var(--brand-accent, #00d782); background: rgba(0, 215, 130, 0.04); }
.sv-oc-drop__text { font-family: 'Inter', sans-serif; font-size: 14px; color: #64748b; }
.sv-oc-drop__hint { font-family: 'Inter', sans-serif; font-size: 12px; color: #94a3b8; margin-top: 4px; }
.sv-oc-drop__file { font-family: 'Inter', sans-serif; font-size: 14px; color: #1a1a1a; font-weight: 600; }
.sv-oc-drop__remove { font-family: 'Inter', sans-serif; font-size: 13px; color: #ef4444; background: none; border: none; cursor: pointer; margin-top: 8px; }

.sv-oc-form__btn { width: 100%; padding: 16px; background: var(--brand-accent, #00d782); color: #1a1a1a; border: none; border-radius: 12px; font-family: 'Inter', sans-serif; font-size: 15px; font-weight: 700; cursor: pointer; transition: transform 200ms, box-shadow 200ms; }
.sv-oc-form__btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0, 215, 130, 0.3); }
.sv-oc-form__btn:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
